import React, { useState, useEffect } from 'react';
import { 
  FaCheck, 
  FaRocket, 
  FaChartLine, 
  FaCode, 
  FaStar, 
  FaClock, 
  FaHandshake,
  FaBrain,
  FaLaptopCode,
  FaBullseye,
  FaMedal, 
  FaTachometerAlt, 
  FaHeadset,
  FaEnvelope
} from 'react-icons/fa';
import './DynamicPricing.css';
import QuoteRequestModal from './QuoteRequestModal';
import portfolioAnalytics from '../utils/analytics';

const DynamicPricing = ({ selectedServices = [] }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // Determine content based on selected services
  const isDigitalMarketing = selectedServices.includes('digital-marketing');
  const isWebDevelopment = selectedServices.includes('web-development');
  const isBothServices = isDigitalMarketing && isWebDevelopment;

  // Dynamic pricing configuration
  const getPricingPackages = () => {
    if (isBothServices) {
      return [
        {
          name: "Startup Digital Ecosystem",
          subtitle: "Perfect for new businesses",
          // Pricing removed - custom quotes only
          duration: "3-4 weeks",
          icon: <FaRocket />,
          popular: false,
          features: [
            "Professional Website (5-7 pages)",
            "Basic E-commerce Setup (if needed)", 
            "Social Media Profile Setup (3 platforms)",
            "Google My Business Optimization",
            "Basic SEO Setup",
            "1 Month Social Media Management",
            "Google Analytics & Tracking Setup",
            "Mobile-First Responsive Design"
          ],
          category: "full-stack-startup"
        },
        {
          name: "Growth Accelerator Package",
          subtitle: "For businesses ready to scale",
          price: "$5,999",
          originalPrice: "$8,500",
          savings: "Save $2,500",
          duration: "5-6 weeks",
          icon: <FaChartLine />,
          popular: true,
          features: [
            "Custom Web Application/E-commerce Platform",
            "Advanced Marketing Automation Setup",
            "Meta Ads + Google Ads Campaign Setup",
            "3 Months Social Media Management",
            "Content Strategy + Creation (30 posts)",
            "Email Marketing Automation",
            "Advanced Analytics & Conversion Tracking",
            "A/B Testing Setup",
            "Weekly Performance Reports"
          ],
          category: "full-stack-growth"
        },
        {
          name: "Enterprise Digital Transformation",
          subtitle: "Complete digital overhaul",
          // Pricing removed - custom quotes only
          duration: "8-10 weeks",
          icon: <FaStar />,
          popular: false,
          features: [
            "Custom Full-Stack Web Platform",
            "Advanced CRM Integration",
            "Multi-Platform Marketing Campaigns",
            "6 Months Complete Digital Management",
            "Video Content Creation (5 videos)",
            "Advanced Marketing Automation",
            "Dedicated Account Manager",
            "Monthly Strategy Calls",
            "Priority Support & Maintenance",
            "Performance Guarantee"
          ],
          category: "full-stack-enterprise"
        }
      ];
    } else if (isDigitalMarketing) {
      return [
        {
          name: "Brand Launch Package",
          subtitle: "Get your brand noticed",
          price: "$1,499",
          originalPrice: "$2,200",
          savings: "Save $700",
          duration: "2-3 weeks",
          icon: <FaBrain />,
          popular: false,
          features: [
            "Brand Strategy & Positioning",
            "Social Media Profile Setup (3 platforms)",
            "Content Strategy + 20 Posts",
            "1 Month Social Media Management",
            "Basic Meta Ads Campaign Setup",
            "Google My Business Optimization",
            "Analytics Setup & First Report"
          ],
          category: "marketing-launch"
        },
        {
          name: "Growth Marketing Engine",
          subtitle: "Scale your brand fast",
          price: "$2,999",
          originalPrice: "$4,200",
          savings: "Save $1,200",
          duration: "4-5 weeks",
          icon: <FaRocket />,
          popular: true,
          features: [
            "Multi-Platform Ad Campaigns (Meta + Google)",
            "3 Months Social Media Management",
            "Content Creation (50 posts + stories)",
            "Email Marketing Automation",
            "Influencer Outreach Strategy",
            "Advanced Analytics & Reporting",
            "A/B Testing & Optimization",
            "Weekly Strategy Calls"
          ],
          category: "marketing-growth"
        },
        {
          name: "Authority Builder Premium",
          subtitle: "Become the industry leader",
          price: "$5,999",
          originalPrice: "$8,500",
          savings: "Save $2,500",
          duration: "6-8 weeks",
          icon: <FaStar />,
          popular: false,
          features: [
            "Complete Brand Authority Campaign",
            "6 Months Full Marketing Management",
            "Video Content Production (8 videos)",
            "Podcast/Speaking Opportunities",
            "Advanced Multi-Platform Campaigns",
            "Thought Leadership Content Strategy",
            "PR & Media Outreach",
            "Personal Brand Development",
            "Monthly Performance Reviews"
          ],
          category: "marketing-authority"
        }
      ];
    } else if (isWebDevelopment) {
      return [
        {
          name: "Professional Website",
          subtitle: "Perfect online presence",
          price: "$1,799",
          originalPrice: "$2,500",
          savings: "Save $700",
          duration: "2-3 weeks",
          icon: <FaLaptopCode />,
          popular: false,
          features: [
            "Custom WordPress Website (5-7 pages)",
            "Mobile-First Responsive Design",
            "SEO Optimization Setup",
            "Google Analytics Integration",
            "Contact Forms & Lead Capture",
            "Speed Optimization",
            "3 Months Free Maintenance",
            "SSL Certificate & Security"
          ],
          category: "web-professional"
        },
        {
          name: "E-commerce Platform",
          subtitle: "Start selling online",
          price: "$3,499",
          originalPrice: "$5,000",
          savings: "Save $1,500",
          duration: "4-5 weeks",
          icon: <FaRocket />,
          popular: true,
          features: [
            "Custom E-commerce Website",
            "Product Catalog Setup (50+ products)",
            "Payment Gateway Integration",
            "Inventory Management System",
            "Customer Account System",
            "Order Management Dashboard",
            "Mobile Shopping Experience",
            "SEO & Performance Optimization",
            "6 Months Support"
          ],
          category: "web-ecommerce"
        },
        {
          name: "Custom Web Application",
          subtitle: "Tailored to your business",
          price: "$7,999",
          originalPrice: "$12,000",
          savings: "Save $4,000",
          duration: "6-8 weeks",
          icon: <FaStar />,
          popular: false,
          features: [
            "Custom React/PHP Application",
            "Database Design & Setup",
            "User Authentication System",
            "Admin Dashboard",
            "API Development & Integration",
            "Advanced Security Features",
            "Scalable Architecture",
            "Performance Monitoring",
            "12 Months Support & Updates"
          ],
          category: "web-custom"
        }
      ];
    }

    // Fallback packages
    return [
      {
        name: "Starter Package",
        subtitle: "Get started right",
        price: "$999",
        originalPrice: "$1,500",
        savings: "Save $500",
        duration: "1-2 weeks",
        icon: <FaRocket />,
        popular: false,
        features: [
          "Basic Website or Marketing Setup",
          "Essential Analytics",
          "1 Month Support",
          "Mobile Optimization"
        ],
        category: "starter"
      }
    ];
  };

  const packages = getPricingPackages();

  // Track pricing view
  useEffect(() => {
    packages.forEach(pkg => {
      portfolioAnalytics.trackPricingView(pkg.category, pkg.price, selectedServices);
    });
  }, [selectedServices]);

  // Handle package selection and quote request
  const handlePackageSelect = (packageData) => {
    setSelectedPackage(packageData);
    
    // Track package selection
    portfolioAnalytics.logEvent({
      type: 'pricing_package_selected',
      timestamp: Date.now(),
      data: {
        packageName: packageData.name,
        packageCategory: packageData.category,
        selectedServices
      }
    });

    // Open quote request modal
    setShowQuoteModal(true);
  };

  const handleGetQuote = (packageData) => {
    // Track quote request
    portfolioAnalytics.trackCTAClick(
      `Get Quote - ${packageData.name}`,
      'quote_request',
      selectedServices,
      'pricing'
    );
    
    // Pre-fill contact form with package info
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      // Store selected package in sessionStorage for contact form
      sessionStorage.setItem('selectedPackage', JSON.stringify(packageData));
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="pricing-section section">
      <div className="container">
        <h2 className="section-title">
          <FaBullseye className="section-icon" />
          {isBothServices ? 'Complete Digital Solutions' : isDigitalMarketing ? 'Marketing Investment Plans' : isWebDevelopment ? 'Website Development Packages' : 'Investment Options'}
        </h2>

        <div className="pricing-intro">
          <h3>
            {isBothServices 
              ? 'Complete Digital Transformation Packages'
              : isDigitalMarketing 
                ? 'Marketing Packages That Actually Drive Results'
                : isWebDevelopment
                  ? 'Professional Website Development Packages'
                  : 'Choose Your Success Package'
            }
          </h3>
          <p className="pricing-subtitle">
            {isBothServices 
              ? 'Get everything you need to build and grow your digital empire. Request a custom quote tailored to your specific needs and budget.'
              : isDigitalMarketing
                ? 'Stop wasting money on marketing that doesn\'t work. These packages deliver measurable ROI - get a personalized quote today.'
                : isWebDevelopment
                  ? 'Get a website that works as hard as you do. Each package is customized to your business needs and budget.'
                  : 'Professional solutions tailored to your needs. Request a custom quote for any package.'
            }
          </p>
        </div>

        <div className="pricing-grid">
          {packages.map((pkg, index) => (
            <div key={index} className={`pricing-card ${pkg.popular ? 'popular' : ''}`}>
              {pkg.popular && <div className="popular-badge">Most Popular</div>}
              
              <div className="pricing-header">
                <div className="package-icon">{pkg.icon}</div>
                <h3 className="package-name">{pkg.name}</h3>
                <p className="package-subtitle">{pkg.subtitle}</p>
                
                <div className="package-info">
                  <div className="duration">
                    <FaClock className="duration-icon" />
                    {pkg.duration}
                  </div>
                  <div className="quote-note">
                    <FaEnvelope className="quote-icon" />
                    Custom Quote Available
                  </div>
                </div>
              </div>

              <div className="pricing-features">
                <ul>
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex}>
                      <FaCheck className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pricing-actions">
                <button 
                  className="btn btn-primary package-select-btn"
                  onClick={() => handlePackageSelect(pkg)}
                >
                  <FaEnvelope />
                  Request Custom Quote
                </button>
                <button 
                  className="btn btn-outline package-info-btn"
                  onClick={() => handleGetQuote(pkg)}
                >
                  <FaHandshake />
                  Let's Discuss This
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="pricing-notes">
          <div className="notes-grid">
            <div className="note-item">
              <h4><FaMedal className="note-icon" /> 100% Satisfaction Guarantee</h4>
              <p>Not happy with the results? We'll work with you until you are, or provide a full refund.</p>
            </div>
            <div className="note-item">
              <h4><FaTachometerAlt className="note-icon" /> Fast Turnaround</h4>
              <p>Most projects completed within the timeline shown. Rush delivery available for urgent projects.</p>
            </div>
            <div className="note-item">
              <h4><FaHeadset className="note-icon" /> Ongoing Support</h4>
              <p>Every package includes post-launch support. You're never left hanging after project completion.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Request Modal */}
      <QuoteRequestModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        selectedPackage={selectedPackage}
        selectedServices={selectedServices}
      />
    </section>
  );
};

export default DynamicPricing;
